// ═══════════════════════════════════════════════════════════════
// Business Owner Portal — My Business (Org Profile Edit)
// View/edit own org profile, no delete
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput, Select,
  Textarea, SimpleGrid,
} from '../../../components/gab/GabComponents';
import { IconPencil, IconDeviceFloppy, IconX } from '@tabler/icons-react';
import { notifications } from '../../../lib/notifications';
import { useMyOrg } from '../../../hooks/useMyOrg';
import { useMainStreetStore } from '../../../store/mainstreetStore';
import type { MSOrganization } from '../../../store/mainstreetStore';
import { SECTOR_TYPES, OP_STATUS } from '../constants';

const SECTOR_SELECT = SECTOR_TYPES.map(s => ({ value: s.id, label: s.label }));
const STATUS_SELECT = OP_STATUS.map(s => ({ value: s.id, label: s.label }));
const INSURANCE_OPTIONS = [
  { value: 'unknown', label: 'Unknown' },
  { value: 'insured', label: 'Insured' },
  { value: 'partial', label: 'Partial' },
  { value: 'uninsured', label: 'Uninsured' },
];
const CONTINUITY_OPTIONS = [
  { value: 'unknown', label: 'Unknown' },
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

function LabelValue({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <Text size="xs" c="dimmed" fw={500}>{label}</Text>
      <Text size="sm">{value || '-'}</Text>
    </div>
  );
}

export default function PortalMyBusiness() {
  const myOrg = useMyOrg();
  const updateOrganization = useMainStreetStore(s => s.updateOrganization);

  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<Partial<MSOrganization>>({});

  useEffect(() => {
    if (myOrg) setForm({ ...myOrg });
  }, [myOrg]);

  const setField = (key: keyof MSOrganization, value: string) =>
    setForm(prev => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    if (!myOrg) return;
    setSaving(true);
    try {
      await updateOrganization(myOrg.id, {
        ...form,
        lastVerified: new Date().toISOString(),
      });
      notifications.show({ message: 'Organization updated', color: 'green' });
      setEditing(false);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Save failed', color: 'red' });
    } finally {
      setSaving(false);
    }
  };

  if (!myOrg) {
    return <Text c="dimmed">No organization linked to your account.</Text>;
  }

  const status = OP_STATUS.find(s => s.id === myOrg.opStatus);

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Group>
          <Title order={4}>{myOrg.name || '(unnamed)'}</Title>
          {status && <Badge color={status.color}>{status.label}</Badge>}
        </Group>
        <Group gap="xs">
          {editing ? (
            <>
              <Button size="xs" variant="light" color="gray" leftSection={<IconX size={14} />}
                onClick={() => { setEditing(false); setForm({ ...myOrg }); }}>Cancel</Button>
              <Button size="xs" leftSection={<IconDeviceFloppy size={14} />}
                loading={saving} onClick={handleSave}>Save</Button>
            </>
          ) : (
            <Button size="xs" variant="light" leftSection={<IconPencil size={14} />}
              onClick={() => setEditing(true)}>Edit</Button>
          )}
        </Group>
      </Group>

      <Paper withBorder p="md">
        {editing ? (
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="sm">
            <TextInput label="Name" value={form.name ?? ''} onChange={e => setField('name', e.target.value)} />
            <Select label="Sector" data={SECTOR_SELECT} value={form.sectorType ?? ''} onChange={v => setField('sectorType', v ?? '')} clearable />
            <TextInput label="Address" value={form.address ?? ''} onChange={e => setField('address', e.target.value)} />
            <TextInput label="City" value={form.city ?? ''} onChange={e => setField('city', e.target.value)} />
            <TextInput label="State" value={form.state ?? ''} onChange={e => setField('state', e.target.value)} />
            <TextInput label="ZIP" value={form.zip ?? ''} onChange={e => setField('zip', e.target.value)} />
            <TextInput label="Phone" value={form.phone ?? ''} onChange={e => setField('phone', e.target.value)} />
            <TextInput label="Email" value={form.email ?? ''} onChange={e => setField('email', e.target.value)} />
            <TextInput label="Primary Contact" value={form.primaryContact ?? ''} onChange={e => setField('primaryContact', e.target.value)} />
            <Select label="Operational Status" data={STATUS_SELECT} value={form.opStatus ?? ''} onChange={v => setField('opStatus', v ?? '')} clearable />
            <Select label="Insurance" data={INSURANCE_OPTIONS} value={form.insurance ?? ''} onChange={v => setField('insurance', v ?? '')} clearable />
            <Select label="Continuity Plan" data={CONTINUITY_OPTIONS} value={form.continuity ?? ''} onChange={v => setField('continuity', v ?? '')} clearable />
            <Textarea label="Notes" value={form.notes ?? ''} onChange={e => setField('notes', e.target.value)} autosize minRows={2} style={{ gridColumn: '1 / -1' }} />
          </SimpleGrid>
        ) : (
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="sm">
            <LabelValue label="Name" value={myOrg.name} />
            <LabelValue label="Sector" value={SECTOR_TYPES.find(s => s.id === myOrg.sectorType)?.label ?? myOrg.sectorType} />
            <LabelValue label="Address" value={myOrg.address} />
            <LabelValue label="City" value={myOrg.city} />
            <LabelValue label="State" value={myOrg.state} />
            <LabelValue label="ZIP" value={myOrg.zip} />
            <LabelValue label="Phone" value={myOrg.phone} />
            <LabelValue label="Email" value={myOrg.email} />
            <LabelValue label="Primary Contact" value={myOrg.primaryContact} />
            <LabelValue label="Status" value={status?.label ?? myOrg.opStatus} />
            <LabelValue label="Insurance" value={myOrg.insurance} />
            <LabelValue label="Continuity Plan" value={myOrg.continuity} />
            <LabelValue label="Last Verified" value={myOrg.lastVerified ? new Date(myOrg.lastVerified).toLocaleDateString() : ''} />
            {myOrg.notes && <div style={{ gridColumn: '1 / -1' }}><LabelValue label="Notes" value={myOrg.notes} /></div>}
          </SimpleGrid>
        )}
      </Paper>
    </div>
  );
}
